import type { ChainConfig } from "./types";

export const AVALANCHE_FUJI_CHAIN_ID = 43113;

/** Rate-limited under load — keep last in the fallback list for reads. */
const AVALANCHE_FUJI_QUIKNODE_RPC_URL =
  "https://twilight-small-rain.avalanche-testnet.quiknode.pro/ad1393483c2713058688a4e0fb47a308f29dd52d/ext/bc/C/rpc/";
const AVALANCHE_FUJI_PUBLICNODE_RPC_URL =
  "https://avalanche-fuji-c-chain-rpc.publicnode.com";
const AVALANCHE_FUJI_AVALANCHE_API_RPC_URL =
  "https://api.avax-test.network/ext/bc/C/rpc";

/** Prefer public RPCs first; QuikNode last (often returns HTTP 429). */
const AVALANCHE_FUJI_RPC_URL = AVALANCHE_FUJI_PUBLICNODE_RPC_URL;
const AVALANCHE_FUJI_RPC_FALLBACK_URLS = [
  AVALANCHE_FUJI_AVALANCHE_API_RPC_URL,
  AVALANCHE_FUJI_QUIKNODE_RPC_URL,
];

/** Underlying ERC-20s from deployConfig.testnet.yaml (Avalanche Fuji v0.2). */
const MTT = "0x537f8f6921ae1ae3f6a60116c9ef95c278302cfc";
const USDC = "0x5425890298aed601595a70AB815c96711a31Bc65";
const WAVAX = "0xd00ae08403B9bbb9124bB305C09058E32C39A48c";

/** Deployed PoD portal pairs from pod-ecosystem-integration deployConfig.testnet.yaml (Fuji v0.2). */
const P_AVAX = "0xa551e0F4afd0C46dCc6860B7538514486988656F";
const P_USDC = "0x6B862584127E02A9dE31Ec944eb3F513C31bb3D9";
const P_MTT = "0x0B197Adda10D1Cb321c4B417Edf1bEaaE4f07D10";
const PORTAL_AVAX = "0x20039A82f5da73C214d64499099A98Ee177412f9";
const PORTAL_USDC = "0xa13F4732D0F1d36f114ad52645436A4217a5b1f4";
const PORTAL_MTT = "0x88F733C9ed387c5c12AFa9D876d4b09a2CF110ce";
const PRIVACY_PORTAL_FACTORY = "0x5737cd8abe31565b98bcc3291e35d7b51e89c17d";

export const avalancheFujiChain: ChainConfig = {
  id: AVALANCHE_FUJI_CHAIN_ID,
  hexId: "0xa869",
  name: "Avalanche Fuji",
  rpcUrl: AVALANCHE_FUJI_RPC_URL,
  rpcFallbackUrls: AVALANCHE_FUJI_RPC_FALLBACK_URLS,
  explorerBaseUrl: "https://testnet.snowscan.xyz",
  priceOracleAddress: "0xc4c0e50cc3307f6ece9f9cbd7c520a67319cae06",
  unlockStrategy: "manual-aes-key",
  portalStrategy: "pod-privacy-portal",
  podFeeEstimation: {
    deposit: { forwardGasLimit: 850_000n, callBackGasLimit: 2_000_000n },
    withdraw: { forwardGasLimit: 900_000n, callBackGasLimit: 2_000_000n },
    // forwardDataSize matches PodERC20.FEE_ESTIMATE_REMOTE_CALL_SIZE (encrypted itUint256).
    transfer: {
      forwardGasLimit: 850_000n,
      callBackGasLimit: 2_000_000n,
      forwardDataSize: 512n,
    },
  },
  addresses: {
    MTT,
    USDC,
    WAVAX,
    "p.MTT": P_MTT,
    "p.USDC": P_USDC,
    "p.AVAX": P_AVAX,
    PrivacyPortalMTT: PORTAL_MTT,
    PrivacyPortalUSDC: PORTAL_USDC,
    PrivacyPortalAVAX: PORTAL_AVAX,
    PrivacyPortalFactory: PRIVACY_PORTAL_FACTORY,
  },
  tokens: [
    {
      symbol: "MTT",
      name: "MyTestToken",
      icon: "/icons/coti.svg",
      decimals: 18,
      isPrivate: false,
      addressKey: "MTT",
      bridgeAddressKey: "PrivacyPortalMTT",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
    {
      symbol: "p.MTT",
      name: "Private MyTestToken",
      icon: "/icons/coti.svg",
      decimals: 18,
      isPrivate: true,
      addressKey: "p.MTT",
      bridgeAddressKey: "PrivacyPortalMTT",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
    {
      symbol: "USDC",
      name: "USD Coin",
      icon: "/icons/USDC.svg",
      decimals: 6,
      isPrivate: false,
      addressKey: "USDC",
      bridgeAddressKey: "PrivacyPortalUSDC",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
    {
      symbol: "p.USDC",
      name: "Private USDC",
      icon: "/icons/USDC.svg",
      decimals: 6,
      isPrivate: true,
      addressKey: "p.USDC",
      bridgeAddressKey: "PrivacyPortalUSDC",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
    {
      symbol: "AVAX",
      name: "Avalanche",
      icon: "/icons/avalanche.svg",
      decimals: 18,
      isPrivate: false,
      isNative: true,
      addressKey: "WAVAX",
      bridgeAddressKey: "PrivacyPortalAVAX",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
    {
      symbol: "p.AVAX",
      name: "Private WAVAX",
      icon: "/icons/avalanche.svg",
      decimals: 18,
      isPrivate: true,
      addressKey: "p.AVAX",
      bridgeAddressKey: "PrivacyPortalAVAX",
      supportedChainIds: [AVALANCHE_FUJI_CHAIN_ID],
    },
  ],
  walletNetwork: {
    chainId: "0xa869",
    chainName: "Avalanche Fuji Testnet",
    rpcUrls: [AVALANCHE_FUJI_RPC_URL, ...AVALANCHE_FUJI_RPC_FALLBACK_URLS],
    nativeCurrency: { name: "Avalanche", symbol: "AVAX", decimals: 18 },
    blockExplorerUrls: ["https://testnet.snowscan.xyz"],
  },
  indexPage: {
    showPodRequestTracker: true,
    amountModalGasLabel: "Estimated Network Gas",
    amountModalGasSymbol: "native",
  },
};
